import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ArrowLeft, LifeBuoy, Loader2, Send } from 'lucide-react';
import { support } from '../../services/superuserApi';

// Alvos de toque ≥ 44 px e texto ≥ 12 px: este painel é usado no telemóvel, a 390 px.
const TAP = 44;

export const STATUS_TABS = [
  { value: '', label: 'Todos' },
  { value: 'Aberto', label: 'Aberto' },
  { value: 'EmCurso', label: 'Em curso' },
  { value: 'Resolvido', label: 'Resolvido' },
  { value: 'Fechado', label: 'Fechado' },
];

export const CHANGEABLE_STATUSES = [
  { value: 'Aberto', label: 'Aberto' },
  { value: 'EmCurso', label: 'Em curso' },
  { value: 'Resolvido', label: 'Resolvido' },
];

const STATUS_STYLE = {
  Aberto: { background: 'var(--info-bg, #e8f0ff)', color: 'var(--info, #1a4fa0)' },
  EmCurso: { background: 'var(--warning-bg, #fff4e5)', color: 'var(--warning, #8a5a00)' },
  Resolvido: { background: 'var(--success-bg, #e7f6ec)', color: 'var(--success, #146c2e)' },
  Fechado: { background: 'rgba(100,116,139,0.15)', color: 'var(--text-muted, #5a6270)' },
};

const PRIORITY_STYLE = {
  Urgente: { background: 'var(--error-bg, #fdeaea)', color: 'var(--error, #a11616)' },
  Alta: { background: 'var(--warning-bg, #fff4e5)', color: 'var(--warning, #8a5a00)' },
  Normal: { background: 'rgba(100,116,139,0.15)', color: 'var(--text-muted, #5a6270)' },
  Baixa: { background: 'rgba(100,116,139,0.10)', color: 'var(--text-muted, #5a6270)' },
};

const fmtDate = (value) => (value
  ? new Date(value).toLocaleString('pt-PT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
  : '—');

const apiMessage = (e, fallback) => e?.response?.data?.message || e?.message || fallback;

const Chip = ({ style, children }) => (
  <span style={{ padding: '2px 8px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700, ...style }}>
    {children}
  </span>
);

/**
 * Pedidos de suporte no PORTAL: contadores por estado, todos os pedidos com o ginásio de origem,
 * a conversa, a resposta, a mudança de estado e o fecho.
 *
 * Isto é o REGISTO do que os ginásios pedem — a «sessão de suporte» (Plataforma) é outra coisa:
 * o operador a entrar no ginásio para fazer. Um pedido fechado recusa mensagens (o servidor
 * também recusa; o ecrã não promete o que a API nega) e reabre-se mudando o estado para Aberto.
 */
const SupportPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [filter, setFilter] = useState('');

  const [thread, setThread] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (status = filter) => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await support.list(status ? { status } : undefined);
      setData(res.data);
    } catch (e) {
      setLoadError(apiMessage(e, 'Não foi possível ler os pedidos de suporte.'));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(filter); }, [load, filter]);

  const openThread = async (id) => {
    setOpenId(id);
    setThread(null);
    setThreadError(null);
    setReply('');
    setThreadLoading(true);
    try {
      const res = await support.get(id);
      setThread(res.data);
    } catch (e) {
      setThreadError(apiMessage(e, 'Não foi possível abrir este pedido.'));
    } finally {
      setThreadLoading(false);
    }
  };

  const sendReply = async (e) => {
    e?.preventDefault?.();
    const text = reply.trim();
    if (!text || !thread) return;
    setBusy(true);
    setThreadError(null);
    try {
      const res = await support.reply(thread.ticket.id, text);
      setThread(res.data);
      setReply('');
      toast.success(res.data?.message || 'Mensagem enviada.');
      await load(filter);
    } catch (err) {
      setThreadError(apiMessage(err, 'Não foi possível enviar a mensagem.'));
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (status) => {
    if (!thread) return;
    setBusy(true);
    setThreadError(null);
    try {
      const res = await support.setStatus(thread.ticket.id, status);
      toast.success(res.data?.message || 'Estado alterado.');
      await openThread(thread.ticket.id);
      await load(filter);
    } catch (err) {
      setThreadError(apiMessage(err, 'Não foi possível mudar o estado.'));
    } finally {
      setBusy(false);
    }
  };

  const close = async () => {
    if (!thread) return;
    setBusy(true);
    setThreadError(null);
    try {
      const res = await support.close(thread.ticket.id);
      toast.success(res.data?.message || 'Pedido fechado.');
      await openThread(thread.ticket.id);
      await load(filter);
    } catch (err) {
      setThreadError(apiMessage(err, 'Não foi possível fechar o pedido.'));
    } finally {
      setBusy(false);
    }
  };

  // ── Conversa ──────────────────────────────────────────────────────────
  if (openId) {
    const ticket = thread?.ticket || null;
    const messages = thread?.messages || [];
    const closed = ticket ? (ticket.status === 'Fechado' || ticket.acceptsMessages === false) : false;

    return (
      <div style={styles.card} data-testid="support-thread">
        <button type="button" onClick={() => { setOpenId(null); setThread(null); }} data-testid="support-thread-back"
          style={{ ...styles.ghost, marginBottom: '0.5rem' }}>
          <ArrowLeft size={16} /> Todos os pedidos
        </button>

        {threadLoading && (
          <div style={styles.muted}>
            <Loader2 size={16} style={{ animation: 'su-spin 1s linear infinite' }} /> A abrir o pedido…
          </div>
        )}

        {!threadLoading && threadError && !ticket && <div style={styles.errorBox}>{threadError}</div>}

        {ticket && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <h3 style={{ ...styles.title, wordBreak: 'break-word' }}>{ticket.subject}</h3>
              <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                <Chip style={STATUS_STYLE[ticket.status] || STATUS_STYLE.Aberto}>{ticket.statusLabel || ticket.status}</Chip>
                <Chip style={PRIORITY_STYLE[ticket.priority] || PRIORITY_STYLE.Normal}>{ticket.priorityLabel || ticket.priority}</Chip>
              </div>
            </div>

            <p style={styles.meta}>
              <strong>{ticket.instanceName}</strong> · aberto por {ticket.createdBy} em {fmtDate(ticket.createdAt)}
              {ticket.statusChangedBy ? ` · ${ticket.statusLabel} por ${ticket.statusChangedBy} em ${fmtDate(ticket.statusChangedAt)}` : ''}
              {ticket.closedBy ? ` · fechado por ${ticket.closedBy} em ${fmtDate(ticket.closedAt)}` : ''}
            </p>

            <ol style={styles.messages} data-testid="support-messages">
              {messages.map((m) => (
                <li key={m.id} style={{
                  ...styles.message,
                  background: m.fromPortal ? 'var(--info-bg, #e8f0ff)' : 'var(--bg-tertiary, #f7f7f7)',
                }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700 }}>
                    {m.authorRoleLabel}
                    <span style={{ fontWeight: 400, color: 'var(--text-muted, #6b6b6b)' }}> · {m.author} · {fmtDate(m.createdAt)}</span>
                  </div>
                  <p style={{ margin: '0.35rem 0 0', fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>{m.body}</p>
                </li>
              ))}
            </ol>

            {closed ? (
              <div style={styles.warningBox} data-testid="support-closed-notice">
                <strong>Este pedido está fechado.</strong> Não aceita mais mensagens de ninguém. Para continuar a falar
                com este ginásio sobre o mesmo assunto, mude o estado para <em>Aberto</em>.
              </div>
            ) : (
              <form onSubmit={sendReply}>
                <label htmlFor="support-portal-reply" style={styles.label}>Resposta ao ginásio</label>
                <textarea
                  id="support-portal-reply"
                  data-testid="support-reply"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  rows={3}
                  maxLength={4000}
                  style={styles.textarea}
                />
                <button type="submit" data-testid="support-send" disabled={busy || !reply.trim()}
                  style={{ ...styles.primary, marginTop: '0.5rem' }}>
                  {busy ? <Loader2 size={16} style={{ animation: 'su-spin 1s linear infinite' }} /> : <Send size={16} />}
                  {busy ? 'A enviar…' : 'Enviar resposta'}
                </button>
              </form>
            )}

            {threadError && <div style={{ ...styles.errorBox, marginTop: '0.5rem' }}>{threadError}</div>}

            <div style={styles.row}>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted, #6b6b6b)', width: '100%' }}>Mudar o estado</span>
              {CHANGEABLE_STATUSES.map((s) => (
                <button key={s.value} type="button" data-testid={`support-status-${s.value}`}
                  onClick={() => changeStatus(s.value)}
                  disabled={busy || ticket.status === s.value}
                  style={{ ...styles.secondary, minHeight: TAP }}>
                  {s.label}
                </button>
              ))}
              {ticket.status !== 'Fechado' && (
                <button type="button" data-testid="support-close" onClick={close} disabled={busy}
                  style={{ ...styles.danger, minHeight: TAP }}>
                  Fechar pedido
                </button>
              )}
            </div>

            <p style={styles.hint}>
              Fechar não apaga nada: fica o autor e a data, o histórico continua legível e o pedido reabre-se mudando o
              estado para Aberto.
            </p>
          </>
        )}
      </div>
    );
  }

  // ── Lista ─────────────────────────────────────────────────────────────
  const counters = data?.counters || {};
  const tickets = data?.tickets || [];

  return (
    <div data-testid="support-panel">
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
            <LifeBuoy size={18} color="var(--accent-primary)" style={{ flexShrink: 0 }} />
            <h3 style={styles.title}>Pedidos de suporte</h3>
          </div>
          <button type="button" onClick={() => load(filter)} data-testid="support-refresh" style={styles.secondary}>
            Actualizar
          </button>
        </div>

        <div style={styles.counters} data-testid="support-counters">
          {[
            ['Aberto', counters.aberto],
            ['Em curso', counters.emCurso],
            ['Resolvido', counters.resolvido],
            ['Fechado', counters.fechado],
          ].map(([label, value]) => (
            <div key={label} style={styles.counter}>
              <span style={{ fontSize: '1.25rem', fontWeight: 700 }}>{value ?? 0}</span>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted, #6b6b6b)' }}>{label}</span>
            </div>
          ))}
        </div>

        <div style={styles.filters}>
          {STATUS_TABS.map((tab) => (
            <button key={tab.value || 'todos'} type="button" data-testid={`support-filter-${tab.value || 'todos'}`}
              onClick={() => setFilter(tab.value)}
              style={{
                ...styles.secondary,
                minHeight: TAP,
                ...(filter === tab.value ? styles.filterActive : null),
              }}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div style={styles.card}>
        {loading && (
          <div style={styles.muted}>
            <Loader2 size={16} style={{ animation: 'su-spin 1s linear infinite' }} /> A ler os pedidos…
          </div>
        )}

        {!loading && loadError && (
          <div style={styles.errorBox} data-testid="support-error">
            <div>{loadError}</div>
            <button type="button" onClick={() => load(filter)} style={{ ...styles.secondary, marginTop: '0.5rem' }}>
              Tentar de novo
            </button>
          </div>
        )}

        {!loading && !loadError && tickets.length === 0 && (
          <p data-testid="support-empty" style={styles.muted}>
            Sem pedidos {filter ? 'neste estado' : 'de momento'}. Quando um ginásio escrever, aparece aqui.
          </p>
        )}

        {!loading && !loadError && tickets.length > 0 && (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '0.5rem' }} data-testid="support-list">
            {tickets.map((ticket) => (
              <li key={ticket.id}>
                <button type="button" data-testid={`support-ticket-${ticket.id}`}
                  onClick={() => openThread(ticket.id)}
                  style={styles.ticketButton}>
                  <span style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', alignItems: 'center' }}>
                    <strong style={{ fontSize: '0.9375rem' }}>{ticket.subject}</strong>
                    <Chip style={STATUS_STYLE[ticket.status] || STATUS_STYLE.Aberto}>{ticket.statusLabel || ticket.status}</Chip>
                    <Chip style={PRIORITY_STYLE[ticket.priority] || PRIORITY_STYLE.Normal}>{ticket.priorityLabel || ticket.priority}</Chip>
                  </span>
                  <span style={{ display: 'block', marginTop: '0.35rem', fontSize: '0.75rem', color: 'var(--text-muted, #6b6b6b)' }}>
                    {ticket.instanceName} · {ticket.createdBy} · última mensagem {fmtDate(ticket.lastMessageAt)} ·{' '}
                    {ticket.messageCount === 1 ? '1 mensagem' : `${ticket.messageCount} mensagens`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const styles = {
  card: {
    background: 'var(--bg-secondary, #fff)',
    border: '1px solid var(--border, #e3e3e3)',
    borderRadius: '16px',
    padding: '1rem',
    marginTop: '1rem',
    color: 'var(--text-primary, #1c1c1c)',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' },
  title: { margin: 0, fontSize: '1rem', fontWeight: 700 },
  counters: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' },
  counter: {
    flex: '1 1 70px', minWidth: '70px', display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: '0.1rem', padding: '0.5rem', borderRadius: '12px', background: 'var(--bg-tertiary, #f7f7f7)',
  },
  filters: { display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.75rem' },
  filterActive: { background: 'var(--accent-primary, #d4a017)', color: '#fff', borderColor: 'transparent' },
  muted: {
    marginTop: '0.75rem', fontSize: '0.8125rem', color: 'var(--text-muted, #6b6b6b)',
    display: 'flex', alignItems: 'center', gap: '0.4rem',
  },
  meta: { margin: '0.5rem 0 0.75rem', fontSize: '0.8125rem', color: 'var(--text-muted, #6b6b6b)', lineHeight: 1.6 },
  messages: { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '0.5rem' },
  message: { border: '1px solid var(--border, #e3e3e3)', borderRadius: '12px', padding: '0.65rem 0.75rem' },
  label: { display: 'block', marginTop: '0.75rem', marginBottom: '0.35rem', fontSize: '0.8125rem', fontWeight: 600 },
  textarea: {
    width: '100%', padding: '0.5rem 0.65rem', borderRadius: '10px', minHeight: TAP,
    border: '1px solid var(--border, #e3e3e3)', background: 'var(--bg-tertiary, #f7f7f7)',
    color: 'var(--text-primary, #1c1c1c)', fontSize: '0.9375rem',
  },
  row: { display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap', marginTop: '0.75rem' },
  ticketButton: {
    width: '100%', textAlign: 'left', minHeight: TAP, cursor: 'pointer', display: 'block', font: 'inherit',
    color: 'inherit', border: '1px solid var(--border, #e3e3e3)', borderRadius: '12px',
    background: 'var(--bg-tertiary, #f7f7f7)', padding: '0.75rem 0.85rem',
  },
  errorBox: {
    marginTop: '0.75rem', padding: '0.75rem', borderRadius: '10px', fontSize: '0.8125rem',
    background: 'var(--error-bg, #fdeaea)', color: 'var(--error-text, #a11616)', lineHeight: 1.5,
  },
  warningBox: {
    marginTop: '0.75rem', padding: '0.75rem', borderRadius: '10px', fontSize: '0.8125rem',
    background: 'var(--warning-bg, #fff7e0)', color: 'var(--warning-text, #7a5200)', lineHeight: 1.6,
  },
  primary: {
    minHeight: TAP, minWidth: TAP, padding: '0 1rem', borderRadius: '10px', border: 'none',
    background: 'var(--accent-primary, #d4a017)', color: '#fff', fontWeight: 700, fontSize: '0.875rem',
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem',
  },
  secondary: {
    minHeight: TAP, padding: '0 0.85rem', borderRadius: '10px', border: '1px solid var(--border, #e3e3e3)',
    background: 'transparent', color: 'var(--text-primary, #1c1c1c)', fontWeight: 600, fontSize: '0.8125rem',
    cursor: 'pointer',
  },
  danger: {
    minHeight: TAP, padding: '0 0.85rem', borderRadius: '10px', border: '1px solid rgba(239,68,68,0.35)',
    background: 'rgba(239,68,68,0.12)', color: 'var(--error, #a11616)', fontWeight: 700, fontSize: '0.8125rem',
    cursor: 'pointer',
  },
  ghost: {
    background: 'none', border: 'none', padding: 0, minHeight: TAP, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8125rem',
    color: 'var(--accent-primary-hover, #1a4fa0)',
  },
  hint: { marginTop: '0.6rem', fontSize: '0.75rem', color: 'var(--text-muted, #6b6b6b)', lineHeight: 1.6 },
};

export default SupportPanel;
